import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

// Tag = short STATUS/PRIORITY label (mono uppercase). Badge = NUMBERS only
// (counts). Don't dress a Badge up as a status label — that's Tag's job.
export type TagVariant = 'crit' | 'high' | 'med' | 'low' | 'done' | 'ok' | 'warn' | 'neutral';

export interface TagProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: TagVariant;
  /** Leading state dot in the label's ink color (calendar connection chips). */
  dot?: boolean;
}

const variantClasses: Record<TagVariant, string> = {
  crit: 'bg-crit-bg text-crit-ink',
  high: 'bg-high-bg text-high-ink',
  med: 'bg-brand-soft text-brand-ink',
  low: 'bg-low-bg text-t2',
  done: 'bg-ok-bg text-ok-ink',
  ok: 'bg-ok-bg text-ok-ink',
  warn: 'bg-warn-bg text-warn-ink',
  neutral: 'bg-rail text-t2',
};

export const Tag = forwardRef<HTMLSpanElement, TagProps>(function Tag(
  { variant = 'neutral', dot = false, className, children, ...rest },
  ref,
) {
  return (
    <span
      ref={ref}
      data-variant={variant}
      className={cn(
        'inline-flex items-center font-mono text-eyebrow font-medium uppercase',
        'rounded-full px-[7px] py-[1px]',
        variantClasses[variant],
        className,
      )}
      {...rest}
    >
      {dot ? (
        <span className="me-1 inline-block h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
      ) : null}
      {children}
    </span>
  );
});
